import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SearchIcon, XIcon, ChevronRightIcon, HeadphonesIcon } from 'lucide-react';
import { samplePromotionalGraphics } from '../data/samplePromotionalGraphics';
import GraphicsLightbox from '../components/Graphics/GraphicsLightbox';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import SaveToFavorites from '../components/UI/SaveToFavorites';
import { FilterSidebar, FilterSidebarWrapper, FilterSection } from '../components/UI/FilterSidebar';
import { SlidersHorizontalIcon } from 'lucide-react';
// Audiobook-specific graphics data (reuses samplePromotionalGraphics + adds narrator-specific types)
const audiobookGraphics = samplePromotionalGraphics.map((g, i) => ({
  ...g,
  id: `audio-${g.id}`,
  type: i % 4 === 0 ? 'narrator card' : i % 4 === 1 ? 'cover art' : i % 4 === 2 ? 'tropes' : 'quote',
  tags: [...(g.tags || []), 'audiobook']
}));
const AudiobookGraphics: React.FC = () => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredGraphics, setFilteredGraphics] = useState(audiobookGraphics);
  const [activeLightbox, setActiveLightbox] = useState<string | null>(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [selectedAuthors, setSelectedAuthors] = useState<string[]>([]);
  const allTypes = [...new Set(audiobookGraphics.map(g => g.type).filter(Boolean))] as string[];
  const allTags = [...new Set(audiobookGraphics.flatMap(g => (g.tags || []).filter(t => t !== 'audiobook')))].slice(0, 12);
  const allAuthors = [...new Set(audiobookGraphics.map(g => g.authorName).filter(Boolean))] as string[];
  const activeFilterCount = selectedTypes.length + selectedTags.length + selectedAuthors.length;
  const filterSections: FilterSection[] = [{
    id: 'type',
    title: 'Graphic Type',
    type: 'checkbox',
    options: allTypes.map(t => ({
      value: t,
      label: t
    })),
    value: selectedTypes,
    onChange: (val: string) => setSelectedTypes(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }, {
    id: 'tags',
    title: 'Tags',
    type: 'checkbox',
    options: allTags.map(t => ({
      value: t,
      label: t
    })),
    value: selectedTags,
    onChange: (val: string) => setSelectedTags(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }, {
    id: 'author',
    title: 'Author',
    type: 'checkbox',
    options: allAuthors.map(a => ({
      value: a,
      label: a
    })),
    value: selectedAuthors,
    onChange: (val: string) => setSelectedAuthors(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val])
  }];
  const resetFilters = () => {
    setSelectedTypes([]);
    setSelectedTags([]);
    setSelectedAuthors([]);
    setSearchQuery('');
  };
  useEffect(() => {
    let result = [...audiobookGraphics];
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(g => g.title?.toLowerCase().includes(query) || g.description?.toLowerCase().includes(query) || g.authorName?.toLowerCase().includes(query) || g.tags?.some(tag => tag.toLowerCase().includes(query)) || g.type?.toLowerCase().includes(query));
    }
    if (selectedTypes.length > 0) {
      result = result.filter(g => g.type && selectedTypes.includes(g.type));
    }
    if (selectedTags.length > 0) {
      result = result.filter(g => g.tags?.some(tag => selectedTags.includes(tag)));
    }
    if (selectedAuthors.length > 0) {
      result = result.filter(g => g.authorName && selectedAuthors.includes(g.authorName));
    }
    setFilteredGraphics(result);
  }, [searchQuery, selectedTypes, selectedTags, selectedAuthors]);
  const activeGraphic = audiobookGraphics.find(g => g.id === activeLightbox);
  return <div className={`min-h-screen ${dk.page}`} data-id="element-5799" data-mp="329sh2qzgc3ju">
      {/* Header */}
      <div className={`py-10 ${dk.surface} border-b ${dk.border}`} data-id="element-5800" data-mp="32bmwxqvv95h2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-5801" data-mp="3h847ifw8eg2k">
          <div className={`flex items-center text-sm mb-4 ${dk.textMuted}`} data-id="element-5802" data-mp="3c76s2b2lw04r">
            <Link to="/" className={`hover:${dk.textPrimary} transition-colors`} data-id="element-5803" data-mp="wg27lguh43sl">
              Home
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-5804" data-mp="rgxzktjacc3u" />
            <Link to="/audiobooks" className={`hover:${dk.textPrimary} transition-colors`} data-id="element-5805" data-mp="z0gx04ta07vy">
              Audiobooks
            </Link>
            <ChevronRightIcon className="h-4 w-4 mx-2" data-id="element-5806" data-mp="q0bpluo319hs" />
            <span className={dk.textPrimary} data-id="element-5807" data-mp="q1v7fppdzslo">Graphics</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6" data-id="element-5808" data-mp="u2oukgv093di">
            <div className="flex items-center gap-3" data-id="element-5809" data-mp="1hs9g97ijmeqh">
              <HeadphonesIcon className={`h-8 w-8 ${rt.textPrimary}`} data-id="element-5810" data-mp="1htkl7anq5sj6" />
              <div data-id="element-5811" data-mp="1mpjdvag35wpe">
                <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-5812" data-mp="1ms5lfa2z8c7m">
                  Audiobook Graphics Gallery
                </h1>
                <p className={`mt-1 ${dk.textSecondary}`} data-id="element-5813" data-mp="1czq32bnrplm7">
                  Narrator cards, cover art, trope graphics and more
                </p>
              </div>
            </div>
            <div className="w-full md:max-w-md relative" data-id="element-5814" data-mp="25gkctyh7hs8y">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-5815" data-mp="25i3sbmv1fz9o">
                <SearchIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-5816" data-mp="2fbzu3ks8o4dh" />
              </div>
              <input type="text" className={`block w-full pl-10 pr-10 py-2.5 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent ${dk.input}`} placeholder="Search audiobook graphics..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-5817" data-mp="2bbpf5imgy3mh" />
              {searchQuery && <button className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setSearchQuery('')} data-id="element-5818" data-mp="1rlx3za7vx8hi">
                  <XIcon className={`h-5 w-5 ${dk.textMuted}`} data-id="element-5819" data-mp="1intj8g4q4fqr" />
                </button>}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-5820" data-mp="18om5vsxzol3n">
        {/* Mobile Filter Button */}
        <div className="mb-6 lg:hidden" data-id="element-5821" data-mp="ykxgjvz92bj9">
          <button onClick={() => setMobileFiltersOpen(true)} className={`flex items-center gap-2 px-4 py-2 rounded-lg border shadow-sm ${dk.card} ${dk.textSecondary}`} data-id="element-5822" data-mp="yjw5vsm5br22">
            <SlidersHorizontalIcon className="h-4 w-4" data-id="element-5823" data-mp="18jd5bsat18pl" />
            Filters
            {activeFilterCount > 0 && <span className={`ml-1 px-1.5 py-0.5 ${rt.badgeRadius} text-xs font-bold text-white ${rt.bgDark}`} data-id="element-5824" data-mp="1dfbzxxmq7myg">
                {activeFilterCount}
              </span>}
          </button>
        </div>

        <div className="flex gap-8" data-id="element-5825" data-mp="pr2jzcc71tf8">
          <FilterSidebarWrapper isOpen={mobileFiltersOpen} onClose={() => setMobileFiltersOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-5826" data-mp="pq0wu9hwlgh5">
            <FilterSidebar sections={filterSections} onReset={resetFilters} data-id="element-5827" data-mp="21hnrrr14zo0" />
          </FilterSidebarWrapper>

          <div className="flex-1 min-w-0" data-id="element-5828" data-mp="1g9rb1yhthg37">
            {filteredGraphics.length > 0 ? <div className="masonry-grid" data-id="element-5829" data-mp="1c8ftmg93k163">
                {filteredGraphics.map(graphic => <div key={graphic.id} className="masonry-item mb-4 break-inside-avoid" onClick={() => setActiveLightbox(graphic.id)} data-id="element-5830" data-mp="1q870zy53yjb4">
                    <div className={`relative overflow-hidden rounded-xl shadow-sm hover:shadow-lg transition-all cursor-pointer group ${dk.card}`} data-id="element-5831" data-mp="1z6ar77r1g499">
                      <img src={graphic.imageUrl} alt={graphic.title || 'Audiobook graphic'} className="w-full h-auto object-cover" data-id="element-5832" data-mp="28wc556pog6ag" />
                      <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10" data-id="element-5833" data-mp="2itqkf2zv6p16">
                        <SaveToFavorites itemId={graphic.id} size="sm" data-id="element-5834" data-mp="2xi0c4nsyk929" />
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end" data-id="element-5835" data-mp="31kn5wu96w423">
                        <div className="p-4 w-full text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300" data-id="element-5836" data-mp="2sj6vwnmae3ye">
                          <h3 className="text-sm font-bold truncate" data-id="element-5837" data-mp="2dszn6zm2zzaw">
                            {graphic.title}
                          </h3>
                          {graphic.authorName && <p className="text-xs opacity-90 mt-0.5" data-id="element-5838" data-mp="24v5vk1z9htu2">
                              by {graphic.authorName}
                            </p>}
                          <div className="flex flex-wrap gap-1 mt-2" data-id="element-5839" data-mp="1uwq05mt7byer">
                            {graphic.tags?.filter(t => t !== 'audiobook').slice(0, 2).map(tag => <span key={tag} className="text-[10px] px-1.5 py-0.5 bg-white/20 rounded-full backdrop-blur-sm" data-id="element-5840" data-mp="1o2oh840rxdhj">
                                  {tag}
                                </span>)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>)}
              </div> : <div className={`text-center py-16 rounded-xl shadow-sm ${dk.card}`} data-id="element-5841" data-mp="1s3i54xe7usaf">
                <HeadphonesIcon className={`h-12 w-12 mx-auto mb-4 ${dk.textMuted}`} data-id="element-5842" data-mp="1x21pwuh2py17" />
                <h3 className={`text-lg font-medium mb-2 ${dk.textHeading}`} data-id="element-5843" data-mp="2kr47617spov6">
                  No graphics found
                </h3>
                <p className={`mb-6 ${dk.textSecondary}`} data-id="element-5844" data-mp="e4ewy8kuk61x">
                  We couldn't find any graphics matching your search.
                </p>
                <button onClick={resetFilters} className={`font-medium ${rt.accentLink}`} data-id="element-5845" data-mp="95lhwbpww6zn">
                  Clear filters
                </button>
              </div>}

            {/* CTA */}
            <div className="mt-16 bg-gradient-to-r from-indigo-600 to-violet-600 rounded-2xl p-8 md:p-12 text-white shadow-xl relative overflow-hidden" data-id="element-5846" data-mp="3rayl67b2twgu">
              <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-white/10 rounded-full blur-3xl" data-id="element-5847" data-mp="3mehwnbzytg38" />
              <div className="absolute bottom-0 left-0 -mb-10 -ml-10 w-40 h-40 bg-indigo-500/20 rounded-full blur-3xl" data-id="element-5848" data-mp="44fr4jftatsr" />
              <div className="relative z-10 text-center md:text-left md:flex md:items-center md:justify-between gap-8" data-id="element-5849" data-mp="1fleck13tz30q">
                <div data-id="element-5850" data-mp="1bjao8bw2c3y1">
                  <h2 className="text-2xl md:text-3xl font-bold mb-3" data-id="element-5851" data-mp="1bjsqbp5epcdv">
                    Are you an author or narrator?
                  </h2>
                  <p className="text-indigo-100 max-w-xl text-lg" data-id="element-5852" data-mp="d89f06fdzjf7">
                    Create and share audiobook graphics — narrator cards, cover
                    reveals, trope graphics — to help listeners discover your
                    work.
                  </p>
                </div>
                <button className="mt-6 md:mt-0 bg-white text-indigo-700 hover:bg-indigo-50 font-bold px-8 py-3.5 rounded-xl transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 whitespace-nowrap" data-id="element-5853" data-mp="3lob6u8q6zs5c">
                  Create Graphics
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {activeGraphic && <GraphicsLightbox graphic={activeGraphic} onClose={() => setActiveLightbox(null)} data-id="element-5854" data-mp="3c6f7780bwvf" />}

      <style jsx data-id="element-5855" data-mp="36ywzf666fksl">{`
        .masonry-grid {
          column-count: 2;
          column-gap: 1rem;
        }
        @media (min-width: 640px) {
          .masonry-grid {
            column-count: 2;
            column-gap: 1.5rem;
          }
        }
        @media (min-width: 768px) {
          .masonry-grid {
            column-count: 3;
          }
        }
        @media (min-width: 1280px) {
          .masonry-grid {
            column-count: 4;
          }
        }
        .masonry-item {
          display: inline-block;
          width: 100%;
          margin-bottom: 1rem;
        }
      `}</style>
    </div>;
};
export default AudiobookGraphics;